import React, { useState, useRef, useEffect } from "react";

/**
 * Reusable searchable single-select dropdown for cities (or any string list).
 *
 * Props:
 * - cities: string[]              full list of options
 * - selected: string               currently selected value ("" = none)
 * - onChange: (newValue: string) => void
 * - loading?: boolean              shows a loading label instead of the list
 * - disabled?: boolean             disables the trigger button
 * - placeholder?: string           text shown when nothing is selected
 * - allowClear?: boolean           show an "All Cities" option to clear selection (default true)
 */
const CitySingleSelectDropdown = ({
  cities = [],
  selected = "",
  onChange,
  loading = false,
  disabled = false,
  placeholder = "Select City",
  allowClear = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    setQuery("");
  };

  const selectCity = (city) => {
    onChange(city); // city === "" clears the selection
    setOpen(false);
    setQuery("");
  };

  const filteredCities = cities.filter((c) =>
    c.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        disabled={disabled}
        className={`w-full border border-gray-300 rounded-md px-3 py-2.5 text-left focus:outline-none focus:border-amber-500 transition bg-white flex justify-between items-center text-sm ${
          disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : ""
        }`}
      >
        <span className={selected ? "text-gray-900" : "text-gray-400"}>
          {loading ? "Loading cities..." : selected || placeholder}
        </span>
        <span className="text-gray-400 text-xs">▼</span>
      </button>

      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cities..."
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-amber-500"
              autoFocus
            />
          </div>

          <div className="overflow-y-auto flex-1">
            {allowClear && (
              <div
                onClick={() => selectCity("")}
                className={`px-4 py-2 text-sm cursor-pointer hover:bg-amber-50 border-b border-gray-100 ${
                  selected === "" ? "bg-amber-100 font-medium text-amber-700" : "text-gray-500"
                }`}
              >
                {placeholder}
              </div>
            )}

            {filteredCities.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-400 text-center">
                No cities found
              </p>
            ) : (
              filteredCities.map((city) => (
                <div
                  key={city}
                  onClick={() => selectCity(city)}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-amber-50 ${
                    selected === city
                      ? "bg-amber-100 font-medium text-amber-700"
                      : "text-gray-700"
                  }`}
                >
                  {city}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySingleSelectDropdown;